import React from 'react';
import { ChevronDown } from 'lucide-react';
import NpiLookup from '../../NpiLookup';

export default function ProviderInfoStep({
  formData,
  setFormData,
  deliveryErrors,
  inputClass,
  savedProviders,
  providersLoading,
  handleSelectProvider,
  handleNpiSelect,
  selectedExistingProvider,
  saveProvider,
  setSaveProvider,
  initialData
}) {
  return (
    <div className="space-y-6">
      <h4 className="font-medium text-slate-900">Provider Information</h4>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Select from your providers
        </label>
        <div className="relative">
          <select
            defaultValue=""
            disabled={providersLoading || savedProviders.length === 0}
            onChange={(e) => {
              handleSelectProvider(e.target.value);
              e.target.value = '';
            }}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none bg-white disabled:bg-slate-50 disabled:text-slate-500"
          >
            <option value="" disabled>
              {providersLoading
                ? 'Loading providers...'
                : savedProviders.length > 0
                  ? 'Choose a saved provider to auto-fill...'
                  : 'No saved providers yet'}
            </option>
            {savedProviders.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.organization ? ` — ${p.organization}` : ''}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {savedProviders.length > 0
            ? 'Or fill in the fields manually below.'
            : 'No saved providers found yet. Fill in the fields below to create a new request.'}
        </p>
      </div>

      <NpiLookup onSelect={handleNpiSelect} initialNpi={formData.providerNpi} />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Provider Name *
          </label>
          <input
            type="text"
            value={formData.providerName}
            onChange={(e) => setFormData({ ...formData, providerName: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Dr. Jane Smith"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Title/Credential
          </label>
          <input
            type="text"
            value={formData.providerTitle}
            onChange={(e) => setFormData({ ...formData, providerTitle: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="MD, DO, NP, etc."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Organization *
          </label>
          <input
            type="text"
            value={formData.organization}
            onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="City Medical Center"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Department
          </label>
          <input
            type="text"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Medical Records, Cardiology, etc."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Address
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          className={inputClass(Boolean(deliveryErrors.address))}
          placeholder="123 Medical Dr"
        />
        {deliveryErrors.address && (
          <p className="text-xs text-red-600 mt-1">{deliveryErrors.address}</p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className={inputClass(Boolean(deliveryErrors.city))}
          />
          {deliveryErrors.city && (
            <p className="text-xs text-red-600 mt-1">{deliveryErrors.city}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
          <input
            type="text"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            className={inputClass(Boolean(deliveryErrors.state))}
            maxLength={2}
          />
          {deliveryErrors.state && (
            <p className="text-xs text-red-600 mt-1">{deliveryErrors.state}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ZIP</label>
          <input
            type="text"
            value={formData.postalCode}
            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
            className={inputClass(Boolean(deliveryErrors.postalCode))}
          />
          {deliveryErrors.postalCode && (
            <p className="text-xs text-red-600 mt-1">{deliveryErrors.postalCode}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className={inputClass(false)}
          />
          <p className="text-xs text-slate-500 mt-1">Optional, but helpful for follow-ups.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Fax</label>
          <input
            type="tel"
            value={formData.fax}
            onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
            className={inputClass(Boolean(deliveryErrors.fax))}
          />
          {deliveryErrors.fax ? (
            <p className="text-xs text-red-600 mt-1">{deliveryErrors.fax}</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Required if you choose fax delivery.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={inputClass(Boolean(deliveryErrors.email))}
          />
          {deliveryErrors.email ? (
            <p className="text-xs text-red-600 mt-1">{deliveryErrors.email}</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Required if you choose email delivery.</p>
          )}
        </div>
      </div>

      {!initialData?.id && !selectedExistingProvider && formData.providerName && formData.organization && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={saveProvider}
              onChange={(e) => setSaveProvider(e.target.checked)}
              className="rounded border-slate-300"
            />
            Save this provider for future requests
          </label>
        </div>
      )}
    </div>
  );
}
